import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import LandingPrimaryCta from '@/components/home/LandingPrimaryCta';

export default function PricingSection() {
  const { t } = useTranslation();

  const includedItems = [
    t('pricing.included1'),
    t('pricing.included2'),
    t('pricing.included3'),
    t('pricing.included4'),
    t('pricing.included5'),
    t('pricing.included6'),
    t('pricing.included7'),
    t('pricing.included8'),
    t('pricing.included9'),
  ];

  const notPromisedItems = [
    t('pricing.notPromised1'),
    t('pricing.notPromised2'),
    t('pricing.notPromised3'),
  ];

  return (
    <section id="pricing" className="py-20" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #0e1b16 100%)' }} data-testid="pricing-section">
      <div className="container mx-auto px-6">
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
              <CheckCircle2 className="w-4 h-4" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">Energy Control</span>
          </div>
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl font-medium text-white"
            style={{ fontFamily: 'Outfit' }}
          >
            {t('pricing.title')}
          </h2>
        </div>

        <div className="flex justify-center mb-10">
          <span className="inline-block bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 text-xs font-semibold px-4 py-1.5 rounded-full">
            {t('pricing.freePreview')}
          </span>
        </div>

        {/* Primary subscription card — Energy Control introductietarief €9,95/m */}
        <div
          className="max-w-2xl mx-auto rounded-2xl border border-emerald-400/30 shadow-2xl p-7 sm:p-9 relative overflow-hidden"
          style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.20) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
          data-testid="pricing-plan-energy-control"
        >
          <h3 className="text-xl sm:text-2xl font-medium text-center mb-3 text-white" style={{ fontFamily: 'Outfit' }}>{t('pricing.planName')}</h3>
          <div className="flex items-baseline justify-center gap-2 mb-1">
            <span
              className="text-5xl font-semibold text-white"
              style={{ fontFamily: 'Outfit' }}
              data-testid="pricing-subscription-amount"
            >
              {t('pricing.subscriptionPrice')}
            </span>
            <span className="text-base text-white/70">
              {t('pricing.subscriptionUnit')}
            </span>
          </div>
          <p
            className="text-xs text-white/55 text-center mb-8 max-w-md mx-auto"
            data-testid="pricing-subscription-subcopy"
          >
            {t('pricing.subscriptionSubcopy')}
          </p>

          <div className="mb-6">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-300/90 mb-3">
              {t('pricing.includedTitle')}
            </p>
            <ul className="space-y-2.5">
              {includedItems.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/80">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-8">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/55 mb-3">
              {t('pricing.notPromisedTitle')}
            </p>
            <ul className="space-y-2">
              {notPromisedItems.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <XCircle className="w-4 h-4 text-white/40 flex-shrink-0 mt-1" />
                  <span className="text-sm text-white/55">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <LandingPrimaryCta
            ctaId="pricing_subscription"
            label={t('pricing.ctaPrimary')}
            testId="pricing-cta-energy-control"
          />
          <p
            className="text-xs text-white/55 text-center mt-3"
            data-testid="pricing-cta-microcopy"
          >
            {t('pricing.ctaMicrocopy')}
          </p>
        </div>

        {/* Secondary add-on card — one-time €59 plan */}
        <div className="max-w-2xl mx-auto mt-8">
          <details
            className="rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-5"
            data-testid="pricing-addon-volledig-bespaarplan"
          >
            <summary className="cursor-pointer text-sm font-medium text-emerald-300 hover:text-emerald-200">
              {t('pricing.addonTitle')}
            </summary>
            <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-4 items-center">
              <div>
                <p className="text-base font-medium text-white">
                  {t('pricing.addonPlanName')} ·{' '}
                  <span className="text-emerald-300">{t('pricing.addonPrice')}</span>
                </p>
                <p className="text-xs text-white/55 mb-1">
                  {t('pricing.addonOneTime')}
                </p>
                <p className="text-sm text-white/75">
                  {t('pricing.addonDescription')}
                </p>
              </div>
              <button
                onClick={() => {
                  const el = document.querySelector('[data-testid="final-cta-section"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="text-sm font-medium text-emerald-300 hover:text-emerald-200 hover:underline underline-offset-2 self-start sm:self-center whitespace-nowrap"
                data-testid="pricing-addon-cta"
              >
                {t('pricing.addonCta')} →
              </button>
            </div>
          </details>
        </div>
      </div>
    </section>
  );
}
